import React from 'react';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Slider,
  Spinner,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';
import {
  Accessibility20Regular,
  BrightnessLow20Regular,
  ChevronDown16Regular,
  DarkTheme20Regular,
  Globe20Regular,
  KeyReset20Regular,
  Person20Regular,
  PersonEdit20Regular,
  SignOut20Regular,
  WeatherMoon20Regular,
  WeatherSunny20Regular
} from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { changeBlueLight, changeBlueLightLevel, changeThemeMode } from '../../redux/actions/Theme.Actions';
import {
  BLUE_LIGHT_LEVEL_STEP,
  BLUE_LIGHT_MAX_LEVEL,
  BLUE_LIGHT_MIN_LEVEL,
  DEFAULT_BLUE_LIGHT_LEVEL
} from '../../theme/blueLight';
import { DEFAULT_THEME_MODE, ThemeMode, isThemeMode } from '../../theme/guideTokens';
import { AppState } from '../../redux/reducers';
import { ApplicationPaths } from '../api-authorization/ApiAuthorizationConstants';
import LanguageSelectorComponent from '../LanguajeSelector/LanguajeSelector.Component';
import { Languaje } from '../../models/Entities/Settings/Languajes';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './UserComponent.css';

// Common Function
import * as CommonFunction from '../../functions/common';

const useStyles = makeStyles({
  // ── Disparador ────────────────────────────────────────────────────────────
  // Un solo renglon a proposito: el root del Button de Fluent lleva
  // `overflow: hidden`, asi que un disparador de dos lineas se recorta contra
  // la barra superior, que ademas tiene alto fijo (50px). Todo en linea entra
  // siempre y no depende de la altura del header.
  trigger: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalSNudge),
    ...shorthands.padding('0', tokens.spacingHorizontalS, '0', tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    minWidth: 0,
    maxWidth: '420px',
    height: '40px'
  },
  // El nombre se muestra entero. El tope es una red de contencion para un
  // nombre desmedido, no un recorte de uso normal: la barra alinea a la
  // derecha y tiene todo el ancho libre, asi que cortar a 150px recortaba
  // nombres corrientes sin ninguna necesidad.
  triggerName: {
    minWidth: 0,
    maxWidth: '280px',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1
  },
  // El idioma vigente queda a la vista sin abrir el menu, como pastilla para
  // que se lea como dato y no como parte del nombre.
  triggerLanguage: {
    flexShrink: 0,
    ...shorthands.padding('1px', tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap'
  },

  // ── Menu ──────────────────────────────────────────────────────────────────
  surface: {
    width: '300px',
    maxWidth: '92vw',
    ...shorthands.padding('0')
  },

  // Cabecera con identidad: antes el menu abria directo en una lista de links
  // y no confirmaba con que cuenta se estaba operando.
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium, tokens.borderRadiusMedium, '0', '0')
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    ...shorthands.gap('1px')
  },
  headerName: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  headerMail: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalXS)
  },
  // Los accesos eran <Link> sueltos, sin area de click ni icono. Como botones
  // ocupan toda la fila y se leen como acciones del menu.
  item: {
    justifyContent: 'flex-start',
    width: '100%',
    fontWeight: tokens.fontWeightRegular
  },
  itemDanger: {
    justifyContent: 'flex-start',
    width: '100%',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPaletteRedForeground1
  },

  languageBlock: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM)
  },
  languageHeading: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalXS),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2
  },

  // Los separadores van de borde a borde del popover: con padding lateral
  // quedaban cortados y se leian como parte de la seccion de arriba en lugar de
  // como el corte entre dos grupos de acciones.
  divider: {
    ...shorthands.padding('0'),
    ...shorthands.margin(tokens.spacingVerticalXXS, '0')
  },
  themeList: {
    ...shorthands.padding('0'),
    minWidth: 0
  },
  // Graduador de la reducción de luz azul: va fuera del MenuList (que captura las flechas
  // para moverse entre opciones) y alineado con el texto de las opciones.
  blueLightLevel: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
    ...shorthands.padding('0', tokens.spacingHorizontalS, tokens.spacingVerticalXS, tokens.spacingHorizontalXXL)
  },
  blueLightLevelRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS)
  },
  blueLightLevelLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2
  },
  blueLightLevelValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontVariantNumeric: 'tabular-nums',
    minWidth: '36px',
    textAlign: 'right'
  },
  blueLightSlider: {
    flexGrow: 1,
    minWidth: 0
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('0', tokens.spacingHorizontalS)
  }
});

export const UserSessionWidgetComponent = () => {

  const styles = useStyles();

  const dispatch = useDispatch();
  const AuthState = useSelector((state: AppState) => state.oidc);
  const themeMode = useSelector((state: AppState) => state.Theme?.mode ?? DEFAULT_THEME_MODE);
  const blueLight = useSelector((state: AppState) => state.Theme?.blueLight ?? false);
  const blueLightLevel = useSelector((state: AppState) => state.Theme?.blueLightLevel ?? DEFAULT_BLUE_LIGHT_LEVEL);
  const CurrentLanguaje = useSelector(
    (state: AppState) => state.UserSettings?.CurrentUser?.UserLanguaje as Languaje | undefined
  );

  //useTranslation()
  const [t, i18n] = useTranslation();

  const logoutPath = `${ApplicationPaths.LogOut}`;

  const profile = AuthState?.user?.profile;
  const fullName = `${profile?.family_name ?? ''}, ${profile?.given_name ?? ''}`;
  const initials = CommonFunction.getNameInitials(
    `${profile?.family_name} ${profile?.given_name}`
  );

  // El store puede no haber resuelto todavia el idioma: se cae al que i18next
  // esta usando de verdad, que es lo que el usuario ve en pantalla.
  const languageCode = (CurrentLanguaje?.code || i18n.language || 'es').toUpperCase();

  const themeOptions: { mode: ThemeMode; labelKey: string; icon: JSX.Element }[] = [
    { mode: 'light', labelKey: 'userSessionWidget.theme.light', icon: <WeatherSunny20Regular /> },
    { mode: 'dark', labelKey: 'userSessionWidget.theme.dark', icon: <WeatherMoon20Regular /> },
    { mode: 'high-contrast', labelKey: 'userSessionWidget.theme.highContrast', icon: <Accessibility20Regular /> }
  ];

  const onThemeChange = (_: unknown, data: { name: string; checkedItems: string[] }) => {
    const selected = data.checkedItems[0];
    if (data.name === 'theme' && isThemeMode(selected) && selected !== themeMode) {
      dispatch(changeThemeMode(selected) as any);
    }
    if (data.name === 'blueLight') {
      const enabled = data.checkedItems.includes('on');
      if (enabled !== blueLight) dispatch(changeBlueLight(enabled) as any);
    }
  };

  const onBlueLightLevelChange = (_: unknown, data: { value: number }) => {
    if (data.value !== blueLightLevel) dispatch(changeBlueLightLevel(data.value) as any);
  };

  if (AuthState?.user?.expired || !profile) {
    return (
      <div className={styles.loading}>
        <Spinner size="tiny" label="" />
      </div>
    );
  }

  return (
    <div className="userWidget">
      <Popover positioning="below-end" withArrow>
        <PopoverTrigger disableButtonEnhancement>
          <Button appearance="subtle" className={styles.trigger}>
            <Avatar
              name={fullName}
              initials={initials}
              size={32}
              color="colorful"
              badge={{ status: 'available' }}
            />
            <span className={styles.triggerName} title={fullName}>{fullName}</span>
            <span className={styles.triggerLanguage} title={t('userSessionWidget.language')}>
              {languageCode}
            </span>
            <ChevronDown16Regular />
          </Button>
        </PopoverTrigger>

        <PopoverSurface className={styles.surface}>
          <div className={styles.header}>
            <Avatar name={fullName} initials={initials} size={40} color="colorful" />
            <div className={styles.headerText}>
              <span className={styles.headerName} title={fullName}>{fullName}</span>
              {profile?.email && (
                <span className={styles.headerMail} title={String(profile.email)}>
                  {String(profile.email)}
                </span>
              )}
            </div>
          </div>

          <div className={styles.section}>
            <Button
              as="a"
              href={process.env.REACT_APP_USERLINK_BASEURL + String(profile?.sub)}
              target="_blank"
              appearance="subtle"
              icon={<Person20Regular />}
              className={styles.item}
            >
              {t('userSessionWidget.myProfile')}
            </Button>
            <Button
              as="a"
              href={process.env.REACT_APP_USERLINK_BASEURL + String(profile?.sub) + '/edit'}
              target="_blank"
              appearance="subtle"
              icon={<PersonEdit20Regular />}
              className={styles.item}
            >
              {t('userSessionWidget.editProfile')}
            </Button>
            <Button
              as="a"
              href={process.env.REACT_APP_USER_CHANGE_PASSWORD_URL}
              target="_blank"
              appearance="subtle"
              icon={<KeyReset20Regular />}
              className={styles.item}
            >
              {t('userSessionWidget.changePassword')}
            </Button>
          </div>

          <Divider className={styles.divider} />

          <div className={styles.languageBlock}>
            <span className={styles.languageHeading}>
              <Globe20Regular style={{ width: 16, height: 16 }} />
              {t('userSessionWidget.language')}
              <Badge appearance="tint" color="informative" size="small">
                {languageCode}
              </Badge>
            </span>
            <LanguageSelectorComponent />
          </div>

          <Divider className={styles.divider} />

          {/* Tema: claro (por defecto, el de la guía), oscuro o alto contraste. Se recuerda por usuario. */}
          <div className={styles.languageBlock}>
            <span className={styles.languageHeading} id="user-widget-theme-heading">
              <DarkTheme20Regular style={{ width: 16, height: 16 }} />
              {t('userSessionWidget.theme.title')}
            </span>
            <MenuList
              className={styles.themeList}
              aria-labelledby="user-widget-theme-heading"
              checkedValues={{ theme: [themeMode], blueLight: blueLight ? ['on'] : [] }}
              onCheckedValueChange={onThemeChange}
              data-testid="user-widget-theme"
            >
              {themeOptions.map((option) => (
                <MenuItemRadio
                  key={option.mode}
                  name="theme"
                  value={option.mode}
                  icon={option.icon}
                  data-testid={`user-widget-theme-${option.mode}`}
                >
                  {t(option.labelKey)}
                </MenuItemRadio>
              ))}
              {/* Reducción de luz azul: filtro cálido independiente del modo, se combina con cualquiera. */}
              <MenuItemCheckbox
                name="blueLight"
                value="on"
                icon={<BrightnessLow20Regular />}
                data-testid="user-widget-theme-blue-light"
              >
                {t('userSessionWidget.theme.blueLight')}
              </MenuItemCheckbox>
            </MenuList>
            {blueLight && (
              <div className={styles.blueLightLevel} data-testid="user-widget-blue-light-level">
                <span className={styles.blueLightLevelLabel} id="user-widget-blue-light-level-label">
                  {t('userSessionWidget.theme.blueLightLevel')}
                </span>
                <div className={styles.blueLightLevelRow}>
                  <Slider
                    className={styles.blueLightSlider}
                    size="small"
                    min={BLUE_LIGHT_MIN_LEVEL}
                    max={BLUE_LIGHT_MAX_LEVEL}
                    step={BLUE_LIGHT_LEVEL_STEP}
                    value={blueLightLevel}
                    onChange={onBlueLightLevelChange}
                    aria-labelledby="user-widget-blue-light-level-label"
                    aria-valuetext={`${blueLightLevel} %`}
                    data-testid="user-widget-blue-light-slider"
                  />
                  <span className={styles.blueLightLevelValue} aria-hidden="true">
                    {blueLightLevel} %
                  </span>
                </div>
              </div>
            )}
          </div>

          <Divider className={styles.divider} />

          <div className={styles.section}>
            <Button
              as="a"
              href={logoutPath}
              target="_self"
              appearance="subtle"
              icon={<SignOut20Regular />}
              className={styles.itemDanger}
            >
              {t('userSessionWidget.signOff')}
            </Button>
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
};
